import { makeChoices, makeTextChoices } from "../distractors";
import { clean, numStr, numUnit, text } from "../format";
import type { QuestionDraft, Rng, TopicDef } from "../types";
import { both, byTier, numeric, q, textAnswer } from "./helpers";

const CM = { pl: "cm", en: "cm" };
const cm = (n: number) => numUnit(n, "cm");
const zl = (n: number) => numUnit(n, "zł", "zł");
const ZL = { pl: "zł", en: "zł" };

/** "5x", "x", "-x", "-3x" */
function coef(c: number): string {
  if (c === 1) return "x";
  if (c === -1) return "-x";
  return `${c}x`;
}
function withConst(c: number, k: number): string {
  if (k === 0) return coef(c);
  if (c === 0) return `${k}`;
  return k > 0 ? `${coef(c)}+${k}` : `${coef(c)}-${-k}`;
}

// ---------------------------------------------------------------- 1. power rules
function powersRules(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const a = rng.pick([2, 3, 5, 7, 10]);
      const m = rng.int(2, 7);
      const n = rng.int(2, 7);
      const choice4 = makeChoices(rng, m + n, [m * n, Math.abs(m - n), m + n + 1], (r) => r.int(2, 20));
      return q(
        both(
          `${a}^${m} · ${a}^${n} = ${a}^? Podaj wykładnik.`,
          `${a}^${m} × ${a}^${n} = ${a}^? Give the exponent.`,
        ),
        numeric(m + n),
        [`Przy mnożeniu potęg o tej samej podstawie dodajemy wykładniki.`, `${m} + ${n} = ${m + n}.`],
        [`When multiplying powers with the same base, add the exponents.`, `${m} + ${n} = ${m + n}.`],
        { choice4 },
      );
    },
    () => {
      const a = rng.pick([2, 3, 5, 10]);
      if (rng.chance(0.5)) {
        const m = rng.int(2, 5);
        const n = rng.int(2, 4);
        const choice4 = makeChoices(rng, m * n, [m + n, m ** n, m * n + 1], (r) => r.int(2, 25));
        return q(
          both(`(${a}^${m})^${n} = ${a}^? Podaj wykładnik.`, `(${a}^${m})^${n} = ${a}^? Give the exponent.`),
          numeric(m * n),
          [`Potęgę potęgi liczymy, mnożąc wykładniki.`, `${m} · ${n} = ${m * n}.`],
          [`A power of a power: multiply the exponents.`, `${m} × ${n} = ${m * n}.`],
          { choice4 },
        );
      }
      const n = rng.int(2, 6);
      const m = n + rng.int(1, 6);
      const choice4 = makeChoices(rng, m - n, [m + n, Math.round(m / n), m - n + 1], (r) => r.int(1, 12));
      return q(
        both(
          `${a}^${m} : ${a}^${n} = ${a}^? Podaj wykładnik.`,
          `${a}^${m} ÷ ${a}^${n} = ${a}^? Give the exponent.`,
        ),
        numeric(m - n),
        [`Przy dzieleniu potęg o tej samej podstawie odejmujemy wykładniki.`, `${m} − ${n} = ${m - n}.`],
        [`When dividing powers with the same base, subtract the exponents.`, `${m} − ${n} = ${m - n}.`],
        { choice4 },
      );
    },
    () => {
      const variant = rng.int(0, 2);
      if (variant === 0) {
        const a = rng.pick([2, 4, 5, 10]);
        const n = rng.int(1, 3);
        const value = clean(a ** -n);
        const choice4 = makeChoices(rng, value, [-(a ** n), -a * n, clean(1 / (a * n))], (r) =>
          clean(r.int(1, 50) / 100),
        );
        return q(
          both(`Oblicz: ${a}^(−${n}) = ?`, `Calculate: ${a}^(−${n}) = ?`),
          numeric(value),
          [
            `Ujemny wykładnik to odwrotność: ${a}^(−${n}) = 1/${a}^${n}.`,
            `1/${a ** n} = ${numStr(value, "pl")}.`,
          ],
          [
            `A negative exponent means the reciprocal: ${a}^(−${n}) = 1/${a}^${n}.`,
            `1/${a ** n} = ${numStr(value, "en")}.`,
          ],
          { choice4 },
        );
      }
      if (variant === 1) {
        const a = rng.int(2, 99);
        const choice4 = makeChoices(rng, 1, [0, a, -1], (r) => r.int(2, 9));
        return q(
          both(`Oblicz: ${a}^0 = ?`, `Calculate: ${a}^0 = ?`),
          numeric(1),
          [`Każda liczba różna od zera podniesiona do potęgi 0 daje 1.`],
          [`Any non-zero number to the power 0 equals 1.`],
          { choice4 },
        );
      }
      const d = rng.int(2, 5);
      const n = rng.int(2, 3);
      const value = d ** n;
      const choice4 = makeChoices(rng, value, [clean(1 / value), -value, d * n], (r) => r.int(2, 130));
      return q(
        both(`Oblicz: (1/${d})^(−${n}) = ?`, `Calculate: (1/${d})^(−${n}) = ?`),
        numeric(value),
        [`Ujemny wykładnik odwraca ułamek: (1/${d})^(−${n}) = ${d}^${n}.`, `${d}^${n} = ${value}.`],
        [`A negative exponent flips the fraction: (1/${d})^(−${n}) = ${d}^${n}.`, `${d}^${n} = ${value}.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 2. roots
function roots(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      if (rng.chance(0.35)) {
        const n = rng.int(2, 10);
        const v = n ** 3;
        const choice4 = makeChoices(rng, n, [v / 3, n + 1, n * n], (r) => r.int(2, 12));
        return q(
          both(`Oblicz: ∛${v} = ?`, `Calculate: ∛${v} = ?`),
          numeric(n),
          [`Szukamy liczby, której sześcian to ${v}.`, `${n}³ = ${v}, więc ∛${v} = ${n}.`],
          [`We need the number whose cube is ${v}.`, `${n}³ = ${v}, so ∛${v} = ${n}.`],
          { choice4 },
        );
      }
      const n = rng.int(2, 20);
      const v = n * n;
      const choice4 = makeChoices(rng, n, [v / 2, n + 1, n - 1], (r) => r.int(2, 25));
      return q(
        both(`Oblicz: √${v} = ?`, `Calculate: √${v} = ?`),
        numeric(n),
        [`Szukamy liczby, której kwadrat to ${v}.`, `${n}² = ${v}, więc √${v} = ${n}.`],
        [`We need the number whose square is ${v}.`, `${n}² = ${v}, so √${v} = ${n}.`],
        { choice4 },
      );
    },
    () => {
      const pairs: Array<[number, number]> = [
        [2, 8],
        [3, 12],
        [2, 18],
        [5, 20],
        [3, 27],
        [2, 32],
        [6, 24],
        [5, 45],
        [7, 28],
        [10, 40],
      ];
      const [a, b] = rng.pick(pairs);
      const v = Math.sqrt(a * b);
      const choice4 = makeChoices(rng, v, [a * b, a + b, v + 1], (r) => r.int(2, 30));
      return q(
        both(`Oblicz: √${a} · √${b} = ?`, `Calculate: √${a} × √${b} = ?`),
        numeric(v),
        [`Iloczyn pierwiastków to pierwiastek iloczynu: √${a} · √${b} = √${a * b}.`, `√${a * b} = ${v}.`],
        [`The product of roots is the root of the product: √${a} × √${b} = √${a * b}.`, `√${a * b} = ${v}.`],
        { choice4 },
      );
    },
    () => {
      let n = rng.int(5, 140);
      while (Number.isInteger(Math.sqrt(n))) n = rng.int(5, 140);
      const lower = Math.floor(Math.sqrt(n));
      const choice4 = makeChoices(rng, lower, [lower + 1, lower - 1, Math.round(n / 2)], (r) => r.int(2, 12));
      return q(
        both(
          `Między którymi kolejnymi liczbami naturalnymi leży √${n}? Podaj mniejszą z nich.`,
          `Between which consecutive whole numbers does √${n} lie? Give the smaller one.`,
        ),
        numeric(lower),
        [
          `${lower}² = ${lower * lower}, a ${lower + 1}² = ${(lower + 1) ** 2}.`,
          `${lower * lower} < ${n} < ${(lower + 1) ** 2}, więc ${lower} < √${n} < ${lower + 1}.`,
        ],
        [
          `${lower}² = ${lower * lower} and ${lower + 1}² = ${(lower + 1) ** 2}.`,
          `${lower * lower} < ${n} < ${(lower + 1) ** 2}, so ${lower} < √${n} < ${lower + 1}.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 3. scientific notation
function sciNotation(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const m = clean(rng.int(11, 99) / 10);
      const k = rng.int(2, 6);
      const value = clean(m * 10 ** k);
      const choice4 = makeChoices(
        rng,
        value,
        [clean(m * 10 ** (k - 1)), clean(m * 10 ** (k + 1)), clean(m * k)],
        (r) => r.int(1, 9) * 10 ** r.int(2, 6),
      );
      return q(
        both(
          `Zapisz liczbę ${numStr(m, "pl")} · 10^${k} bez potęgi.`,
          `Write ${numStr(m, "en")} × 10^${k} as an ordinary number.`,
        ),
        numeric(value),
        [
          `Mnożenie przez 10^${k} przesuwa przecinek o ${k} miejsca w prawo.`,
          `${numStr(m, "pl")} · 10^${k} = ${numStr(value, "pl")}.`,
        ],
        [
          `Multiplying by 10^${k} moves the decimal point ${k} places to the right.`,
          `${numStr(m, "en")} × 10^${k} = ${numStr(value, "en")}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const m = clean(rng.int(11, 99) / 10);
      const k = rng.int(3, 8);
      const value = clean(m * 10 ** k);
      const choice4 = makeChoices(rng, k, [k + 1, k - 1, String(Math.round(value)).length], (r) =>
        r.int(2, 9),
      );
      return q(
        both(
          `Liczbę ${numStr(value, "pl")} zapisano jako ${numStr(m, "pl")} · 10^k. Podaj k.`,
          `The number ${numStr(value, "en")} is written as ${numStr(m, "en")} × 10^k. Give k.`,
        ),
        numeric(k),
        [
          `Przesuwamy przecinek tak, aby przed nim została jedna cyfra: ${numStr(m, "pl")}.`,
          `Przesunęliśmy go o ${k} miejsc, więc k = ${k}.`,
        ],
        [
          `Move the decimal point so one digit stays in front: ${numStr(m, "en")}.`,
          `It moved ${k} places, so k = ${k}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 4);
      const b = rng.pick([2, 3].filter((x) => a * x < 10));
      const k1 = rng.int(2, 5);
      const k2 = rng.int(2, 5);
      const choice4 = makeChoices(rng, k1 + k2, [k1 * k2, Math.abs(k1 - k2), k1 + k2 + 1], (r) =>
        r.int(2, 12),
      );
      return q(
        both(
          `(${a} · 10^${k1}) · (${b} · 10^${k2}) = ${a * b} · 10^k. Podaj k.`,
          `(${a} × 10^${k1}) × (${b} × 10^${k2}) = ${a * b} × 10^k. Give k.`,
        ),
        numeric(k1 + k2),
        [`Mnożymy liczby: ${a} · ${b} = ${a * b}, a wykładniki dodajemy.`, `${k1} + ${k2} = ${k1 + k2}.`],
        [
          `Multiply the numbers: ${a} × ${b} = ${a * b}, and add the exponents.`,
          `${k1} + ${k2} = ${k1 + k2}.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 4. simplify expressions
function algebraSimplify(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  const asText = (s: string) => ({ pl: s, en: s });
  return byTier(
    tier,
    () => {
      const a = rng.int(2, 9);
      const b = rng.int(1, 9);
      const sub = rng.chance(0.4) && a !== b;
      const c = sub ? a - b : a + b;
      const expr = sub ? `${a}x − ${b}x` : `${a}x + ${b}x`;
      const answer = coef(c);
      const choice4 = makeTextChoices(
        rng,
        asText(answer),
        [asText(`${a * b}x`), asText(`${c}x²`), asText(`${sub ? a + b : a - b}x`), asText(`${c}`)],
        (i) => asText(`${c + i + 2}x`),
      );
      return q(
        both(`Uprość wyrażenie: ${expr}`, `Simplify: ${expr}`),
        textAnswer(answer, [`${c}·x`, `${c}*x`]),
        [
          `Wyrazy podobne (z tym samym x) ${sub ? "odejmujemy" : "dodajemy"}: ${a} ${sub ? "−" : "+"} ${b} = ${c}.`,
          `Wynik: ${answer}.`,
        ],
        [`Combine like terms (same x): ${a} ${sub ? "−" : "+"} ${b} = ${c}.`, `Result: ${answer}.`],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 7);
      const b = rng.int(1, 9);
      const minus = rng.chance(0.4);
      const k = minus ? -a * b : a * b;
      const expr = minus ? `${a}(x − ${b})` : `${a}(x + ${b})`;
      const answer = withConst(a, k);
      const alt = k >= 0 ? `${k}+${coef(a)}` : `-${-k}+${coef(a)}`;
      const choice4 = makeTextChoices(
        rng,
        asText(answer),
        [
          asText(withConst(a, minus ? -b : b)),
          asText(withConst(1, k)),
          asText(withConst(a, -k)),
          asText(`${a}x·${b}`),
        ],
        (i) => asText(withConst(a + i + 1, k + i + 1)),
      );
      return q(
        both(`Rozwiń nawias: ${expr}`, `Expand the bracket: ${expr}`),
        textAnswer(answer, [alt]),
        [
          `Mnożymy ${a} przez każdy składnik w nawiasie.`,
          `${a} · x = ${a}x, ${a} · (${minus ? "−" : ""}${b}) = ${k}. Wynik: ${answer}.`,
        ],
        [
          `Multiply ${a} by each term inside the bracket.`,
          `${a} × x = ${a}x, ${a} × (${minus ? "−" : ""}${b}) = ${k}. Result: ${answer}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 9);
      const b = rng.int(1, 9);
      const c = rng.int(1, 9);
      const d = rng.int(1, 9);
      const cx = a + c;
      const k = b - d;
      const expr = `${a}x + ${b} + ${c}x − ${d}`;
      const answer = withConst(cx, k);
      const alt = k === 0 ? [] : [k > 0 ? `${k}+${coef(cx)}` : `-${-k}+${coef(cx)}`];
      const choice4 = makeTextChoices(
        rng,
        asText(answer),
        [
          asText(withConst(cx, b + d)),
          asText(withConst(a * c, k)),
          asText(withConst(cx, -k)),
          asText(withConst(cx + 1, k)),
        ],
        (i) => asText(withConst(cx + i + 2, k - i - 1)),
      );
      return q(
        both(`Uprość wyrażenie: ${expr}`, `Simplify: ${expr}`),
        textAnswer(answer, alt),
        [`Wyrazy z x: ${a}x + ${c}x = ${cx}x. Liczby: ${b} − ${d} = ${k}.`, `Wynik: ${answer}.`],
        [`Terms with x: ${a}x + ${c}x = ${cx}x. Numbers: ${b} − ${d} = ${k}.`, `Result: ${answer}.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 5. linear equations
function linearEquations(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const x = rng.int(-9, 12);
      const a = rng.int(2, 9);
      const b = rng.int(-15, 15);
      const c = a * x + b;
      const eq = `${a}x ${b >= 0 ? "+" : "−"} ${Math.abs(b)} = ${c}`;
      const choice4 = makeChoices(rng, x, [-x, x + 1, x - 1, c - b], (r) => r.int(-12, 12));
      return q(
        both(`Rozwiąż równanie: ${eq}`, `Solve: ${eq}`),
        numeric(x),
        [
          `Przenosimy ${b} na prawą stronę: ${a}x = ${c} ${b >= 0 ? "−" : "+"} ${Math.abs(b)} = ${c - b}.`,
          `Dzielimy przez ${a}: x = ${x}.`,
        ],
        [
          `Move ${b} to the right: ${a}x = ${c} ${b >= 0 ? "−" : "+"} ${Math.abs(b)} = ${c - b}.`,
          `Divide by ${a}: x = ${x}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const x = rng.int(-8, 10);
      const a = rng.int(2, 9);
      let c = rng.int(1, 8);
      while (c === a) c = rng.int(1, 8);
      const b = rng.int(-12, 12);
      const d = a * x + b - c * x;
      const eq = `${a}x ${b >= 0 ? "+" : "−"} ${Math.abs(b)} = ${c}x ${d >= 0 ? "+" : "−"} ${Math.abs(d)}`;
      const choice4 = makeChoices(rng, x, [-x, x + 1, x - 2], (r) => r.int(-12, 12));
      return q(
        both(`Rozwiąż równanie: ${eq}`, `Solve: ${eq}`),
        numeric(x),
        [
          `Niewiadome na lewo, liczby na prawo: ${a}x − ${c}x = ${d} − (${b}).`,
          `${a - c}x = ${d - b}, więc x = ${x}.`,
        ],
        [
          `Unknowns to the left, numbers to the right: ${a}x − ${c}x = ${d} − (${b}).`,
          `${a - c}x = ${d - b}, so x = ${x}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const x = rng.int(-6, 9);
      const a = rng.int(2, 6);
      const b = rng.int(1, 9);
      const minus = rng.chance(0.5);
      const c = a * (minus ? x - b : x + b);
      const eq = `${a}(x ${minus ? "−" : "+"} ${b}) = ${c}`;
      const choice4 = makeChoices(rng, x, [-x, x + b, c / a], (r) => r.int(-10, 12));
      return q(
        both(`Rozwiąż równanie: ${eq}`, `Solve: ${eq}`),
        numeric(x),
        [
          `Dzielimy obie strony przez ${a}: x ${minus ? "−" : "+"} ${b} = ${c / a}.`,
          `x = ${c / a} ${minus ? "+" : "−"} ${b} = ${x}.`,
        ],
        [
          `Divide both sides by ${a}: x ${minus ? "−" : "+"} ${b} = ${c / a}.`,
          `x = ${c / a} ${minus ? "+" : "−"} ${b} = ${x}.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 6. proportions
function proportions(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const unit = rng.int(2, 9);
      const n1 = rng.int(2, 6);
      const n2 = n1 + rng.int(1, 6);
      const price1 = unit * n1;
      const price2 = unit * n2;
      const choice4 = makeChoices(
        rng,
        price2,
        [price1 + n2, price1 * 2, price2 + unit],
        (r) => r.int(5, 120),
        zl,
      );
      return q(
        both(
          `Za ${n1} kg jabłek zapłacono ${price1} zł. Ile kosztuje ${n2} kg jabłek?`,
          `${n1} kg of apples cost ${price1} zł. How much do ${n2} kg cost?`,
        ),
        numeric(price2, ZL),
        [`Cena 1 kg: ${price1} : ${n1} = ${unit} zł.`, `${n2} kg: ${n2} · ${unit} = ${price2} zł.`],
        [`Price per kg: ${price1} ÷ ${n1} = ${unit} zł.`, `${n2} kg: ${n2} × ${unit} = ${price2} zł.`],
        { choice4 },
      );
    },
    () => {
      const perLitre = rng.int(8, 16);
      const litres1 = rng.int(3, 8);
      const litres2 = litres1 + rng.int(2, 10);
      const km1 = perLitre * litres1;
      const km2 = perLitre * litres2;
      const choice4 = makeChoices(
        rng,
        km2,
        [km1 + litres2, km1 * 2, km2 - perLitre],
        (r) => r.int(20, 300),
        (n) => numUnit(n, "km"),
      );
      return q(
        both(
          `Samochód na ${litres1} litrach paliwa przejeżdża ${km1} km. Ile przejedzie na ${litres2} litrach?`,
          `A car drives ${km1} km on ${litres1} litres of fuel. How far does it go on ${litres2} litres?`,
        ),
        numeric(km2, { pl: "km", en: "km" }),
        [
          `Na 1 litrze: ${km1} : ${litres1} = ${perLitre} km.`,
          `${litres2} litrów: ${litres2} · ${perLitre} = ${km2} km.`,
        ],
        [
          `On 1 litre: ${km1} ÷ ${litres1} = ${perLitre} km.`,
          `${litres2} litres: ${litres2} × ${perLitre} = ${km2} km.`,
        ],
        { choice4 },
      );
    },
    () => {
      const workers1 = rng.int(2, 6);
      const days1 = rng.int(2, 6) * 2;
      const total = workers1 * days1;
      const workers2 = rng.pick([1, 2, 3, 4, 6, 8, 12].filter((w) => w !== workers1 && total % w === 0));
      const days2 = total / workers2;
      const choice4 = makeChoices(
        rng,
        days2,
        [(days1 * workers2) / workers1, days1 + workers2 - workers1, days2 + 1],
        (r) => r.int(1, 30),
        (n) => numUnit(n, "dni", "days"),
      );
      return q(
        both(
          `${workers1} robotników buduje most w ${days1} dni. W ile dni zbuduje go ${workers2} robotników (pracując tak samo szybko)?`,
          `${workers1} workers build a bridge in ${days1} days. How many days would ${workers2} workers need (working at the same pace)?`,
        ),
        numeric(days2, { pl: "dni", en: "days" }),
        [
          `To proporcjonalność odwrotna: więcej robotników = mniej dni. Pracy jest ${workers1} · ${days1} = ${total} „robotniko-dni”.`,
          `${total} : ${workers2} = ${days2} dni.`,
        ],
        [
          `Inverse proportion: more workers = fewer days. The job is ${workers1} × ${days1} = ${total} worker-days.`,
          `${total} ÷ ${workers2} = ${days2} days.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 7. percent problems
function percentProblems(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const price = rng.int(3, 30) * 10;
      const p = rng.pick([5, 10, 15, 20, 25, 30, 40, 50]);
      const up = rng.chance(0.5);
      const change = (price * p) / 100;
      const value = up ? price + change : price - change;
      const choice4 = makeChoices(
        rng,
        value,
        [up ? price - change : price + change, price + p, change],
        (r) => r.int(1, 40) * 10,
        zl,
      );
      return q(
        both(
          `Cena ${price} zł ${up ? "wzrosła" : "spadła"} o ${p}%. Ile wynosi nowa cena?`,
          `A price of ${price} zł ${up ? "rose" : "fell"} by ${p}%. What is the new price?`,
        ),
        numeric(value, ZL),
        [`${p}% z ${price} zł = ${change} zł.`, `${price} ${up ? "+" : "−"} ${change} = ${value} zł.`],
        [`${p}% of ${price} zł = ${change} zł.`, `${price} ${up ? "+" : "−"} ${change} = ${value} zł.`],
        { choice4 },
      );
    },
    () => {
      const original = rng.int(4, 30) * 10;
      const p = rng.pick([10, 20, 25, 30, 50]);
      const now = (original * (100 - p)) / 100;
      const choice4 = makeChoices(
        rng,
        original,
        [now + (now * p) / 100, now + p, now * 2],
        (r) => r.int(4, 40) * 10,
        zl,
      );
      return q(
        both(
          `Po obniżce o ${p}% kurtka kosztuje ${now} zł. Ile kosztowała przed obniżką?`,
          `After a ${p}% discount a jacket costs ${now} zł. What was the price before?`,
        ),
        numeric(original, ZL),
        [
          `Po obniżce zostaje ${100 - p}% ceny, czyli ${now} zł = ${100 - p}% ceny.`,
          `1% = ${now} : ${100 - p} = ${now / (100 - p)} zł, więc 100% = ${original} zł.`,
        ],
        [
          `After the discount ${100 - p}% of the price remains, so ${now} zł = ${100 - p}%.`,
          `1% = ${now} ÷ ${100 - p} = ${now / (100 - p)} zł, so 100% = ${original} zł.`,
        ],
        { choice4 },
      );
    },
    () => {
      const netto = rng.int(1, 30) * 100;
      const vat = rng.pick([23, 8, 5]);
      const brutto = clean(netto * (1 + vat / 100));
      const choice4 = makeChoices(
        rng,
        brutto,
        [netto + vat, clean(netto * (1 - vat / 100)), brutto + 10],
        (r) => r.int(1, 40) * 100,
        zl,
      );
      return q(
        both(
          `Cena netto wynosi ${netto} zł, VAT to ${vat}%. Ile wynosi cena brutto?`,
          `The net price is ${netto} zł and VAT is ${vat}%. What is the gross price?`,
        ),
        numeric(brutto, ZL),
        [
          `VAT: ${vat}% z ${netto} = ${clean((netto * vat) / 100)} zł.`,
          `Brutto = netto + VAT = ${netto} + ${clean((netto * vat) / 100)} = ${numStr(brutto, "pl")} zł.`,
        ],
        [
          `VAT: ${vat}% of ${netto} = ${clean((netto * vat) / 100)} zł.`,
          `Gross = net + VAT = ${netto} + ${clean((netto * vat) / 100)} = ${numStr(brutto, "en")} zł.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 8. Pythagoras
const TRIPLES: Array<[number, number, number]> = [
  [3, 4, 5],
  [6, 8, 10],
  [5, 12, 13],
  [9, 12, 15],
  [8, 15, 17],
  [12, 16, 20],
  [7, 24, 25],
  [15, 20, 25],
  [10, 24, 26],
  [20, 21, 29],
];

function pythagoras(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  const [a, b, c] = rng.pick(TRIPLES);
  return byTier(
    tier,
    () => {
      const choice4 = makeChoices(rng, c, [a + b, c + 1, Math.abs(b - a)], (r) => r.int(5, 30), cm);
      return q(
        both(
          `Przyprostokątne trójkąta prostokątnego mają ${a} cm i ${b} cm. Jaką długość ma przeciwprostokątna?`,
          `The legs of a right triangle are ${a} cm and ${b} cm. How long is the hypotenuse?`,
        ),
        numeric(c, CM),
        [
          `Twierdzenie Pitagorasa: a² + b² = c².`,
          `${a}² + ${b}² = ${a * a} + ${b * b} = ${c * c}, więc c = √${c * c} = ${c} cm.`,
        ],
        [
          `Pythagoras: a² + b² = c².`,
          `${a}² + ${b}² = ${a * a} + ${b * b} = ${c * c}, so c = √${c * c} = ${c} cm.`,
        ],
        { choice4 },
      );
    },
    () => {
      const choice4 = makeChoices(
        rng,
        a,
        [c - b, a + 1, Math.round(Math.sqrt(c * c + b * b))],
        (r) => r.int(3, 25),
        cm,
      );
      return q(
        both(
          `Przeciwprostokątna trójkąta prostokątnego ma ${c} cm, a jedna przyprostokątna ${b} cm. Jaką długość ma druga przyprostokątna?`,
          `The hypotenuse of a right triangle is ${c} cm and one leg is ${b} cm. How long is the other leg?`,
        ),
        numeric(a, CM),
        [`a² = c² − b².`, `${c}² − ${b}² = ${c * c} − ${b * b} = ${a * a}, więc a = √${a * a} = ${a} cm.`],
        [`a² = c² − b².`, `${c}² − ${b}² = ${c * c} − ${b * b} = ${a * a}, so a = √${a * a} = ${a} cm.`],
        { choice4 },
      );
    },
    () => {
      const choice4 = makeChoices(
        rng,
        c,
        [a + b, c - 1, b],
        (r) => r.int(5, 30),
        (n) => numUnit(n, "m"),
      );
      return q(
        both(
          `Drabina opiera się o ścianę. Jej dolny koniec stoi ${a} m od ściany, a górny sięga ${b} m nad ziemię. Jaka jest długość drabiny?`,
          `A ladder leans against a wall. Its foot is ${a} m from the wall and its top reaches ${b} m up. How long is the ladder?`,
        ),
        numeric(c, { pl: "m", en: "m" }),
        [
          `Drabina, ściana i ziemia tworzą trójkąt prostokątny; drabina to przeciwprostokątna.`,
          `${a}² + ${b}² = ${a * a + b * b} = ${c}², więc drabina ma ${c} m.`,
        ],
        [
          `The ladder, wall and ground form a right triangle; the ladder is the hypotenuse.`,
          `${a}² + ${b}² = ${a * a + b * b} = ${c}², so the ladder is ${c} m long.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 9. coordinates
function coordinates(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const y = rng.int(-6, 6);
      const x1 = rng.int(-8, 4);
      const x2 = x1 + rng.int(2, 9);
      const d = x2 - x1;
      const choice4 = makeChoices(rng, d, [x1 + x2, d + 1, Math.abs(x1)], (r) => r.int(1, 15));
      return q(
        both(
          `Punkty A = (${x1}, ${y}) i B = (${x2}, ${y}) leżą na jednej prostej poziomej. Jaka jest długość odcinka AB?`,
          `Points A = (${x1}, ${y}) and B = (${x2}, ${y}) lie on one horizontal line. What is the length of AB?`,
        ),
        numeric(d),
        [
          `Punkty mają tę samą współrzędną y, więc odległość to różnica współrzędnych x.`,
          `${x2} − (${x1}) = ${d}.`,
        ],
        [
          `The points share the y coordinate, so the distance is the difference of the x coordinates.`,
          `${x2} − (${x1}) = ${d}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const x1 = rng.int(-8, 8);
      const x2 = x1 + 2 * rng.int(-5, 5);
      const y1 = rng.int(-8, 8);
      const y2 = y1 + 2 * rng.int(-5, 5);
      const askX = rng.chance(0.5);
      const mid = askX ? (x1 + x2) / 2 : (y1 + y2) / 2;
      const choice4 = makeChoices(
        rng,
        mid,
        [askX ? x1 + x2 : y1 + y2, mid + 1, askX ? (y1 + y2) / 2 : (x1 + x2) / 2],
        (r) => r.int(-8, 8),
      );
      return q(
        both(
          `Podaj współrzędną ${askX ? "x" : "y"} środka odcinka o końcach A = (${x1}, ${y1}) i B = (${x2}, ${y2}).`,
          `Give the ${askX ? "x" : "y"} coordinate of the midpoint of the segment with ends A = (${x1}, ${y1}) and B = (${x2}, ${y2}).`,
        ),
        numeric(mid),
        [
          `Środek odcinka to średnia współrzędnych końców.`,
          askX ? `(${x1} + ${x2}) : 2 = ${mid}.` : `(${y1} + ${y2}) : 2 = ${mid}.`,
        ],
        [
          `The midpoint is the average of the end coordinates.`,
          askX ? `(${x1} + ${x2}) ÷ 2 = ${mid}.` : `(${y1} + ${y2}) ÷ 2 = ${mid}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const [a, b, c] = rng.pick(TRIPLES.slice(0, 5));
      const x1 = rng.int(-5, 5);
      const y1 = rng.int(-5, 5);
      const x2 = x1 + a;
      const y2 = y1 + b;
      const choice4 = makeChoices(rng, c, [a + b, c + 1, a * b], (r) => r.int(3, 30));
      return q(
        both(
          `Oblicz długość odcinka o końcach A = (${x1}, ${y1}) i B = (${x2}, ${y2}).`,
          `Find the length of the segment with ends A = (${x1}, ${y1}) and B = (${x2}, ${y2}).`,
        ),
        numeric(c),
        [
          `Różnice współrzędnych: ${a} i ${b}. To przyprostokątne trójkąta prostokątnego.`,
          `√(${a}² + ${b}²) = √${a * a + b * b} = ${c}.`,
        ],
        [
          `Coordinate differences: ${a} and ${b}. These are the legs of a right triangle.`,
          `√(${a}² + ${b}²) = √${a * a + b * b} = ${c}.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 10. statistics
function statisticsBasic(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  const list = (n: number) => Array.from({ length: n }, () => rng.int(1, 30));
  return byTier(
    tier,
    () => {
      const mean = rng.int(5, 25);
      const devs = Array.from({ length: 4 }, () => rng.int(-4, 4));
      devs.push(-devs.reduce((a, b) => a + b, 0));
      const nums = rng.shuffle(devs.map((d) => mean + d));
      const sum = nums.reduce((a, b) => a + b, 0);
      const choice4 = makeChoices(rng, mean, [sum, mean + 1, mean - 1, Math.round(sum / 4)], (r) =>
        r.int(2, 40),
      );
      return q(
        both(
          `Oblicz średnią arytmetyczną liczb: ${nums.join(", ")}.`,
          `Find the mean of: ${nums.join(", ")}.`,
        ),
        numeric(mean),
        [`Suma: ${nums.join(" + ")} = ${sum}.`, `Średnia: ${sum} : 5 = ${mean}.`],
        [`Sum: ${nums.join(" + ")} = ${sum}.`, `Mean: ${sum} ÷ 5 = ${mean}.`],
        { choice4 },
      );
    },
    () => {
      const nums = list(5);
      const sorted = [...nums].sort((a, b) => a - b);
      const median = sorted[2] as number;
      const choice4 = makeChoices(
        rng,
        median,
        [
          nums[2] as number,
          Math.round(nums.reduce((a, b) => a + b, 0) / 5),
          sorted[1] as number,
          sorted[3] as number,
        ],
        (r) => r.int(1, 30),
      );
      return q(
        both(`Wyznacz medianę liczb: ${nums.join(", ")}.`, `Find the median of: ${nums.join(", ")}.`),
        numeric(median),
        [
          `Porządkujemy rosnąco: ${sorted.join(", ")}.`,
          `Mediana to środkowa (trzecia z pięciu) liczba: ${median}.`,
        ],
        [
          `Sort ascending: ${sorted.join(", ")}.`,
          `The median is the middle (third of five) number: ${median}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const nums = list(6);
      const sorted = [...nums].sort((a, b) => a - b);
      const median = clean(((sorted[2] as number) + (sorted[3] as number)) / 2);
      const choice4 = makeChoices(
        rng,
        median,
        [sorted[2] as number, sorted[3] as number, clean(nums.reduce((a, b) => a + b, 0) / 6)],
        (r) => r.int(1, 30),
      );
      return q(
        both(`Wyznacz medianę liczb: ${nums.join(", ")}.`, `Find the median of: ${nums.join(", ")}.`),
        numeric(median),
        [
          `Porządkujemy rosnąco: ${sorted.join(", ")}.`,
          `Przy parzystej liczbie danych mediana to średnia dwóch środkowych: (${sorted[2]} + ${sorted[3]}) : 2 = ${numStr(median, "pl")}.`,
        ],
        [
          `Sort ascending: ${sorted.join(", ")}.`,
          `With an even count the median is the mean of the two middle values: (${sorted[2]} + ${sorted[3]}) ÷ 2 = ${numStr(median, "en")}.`,
        ],
        { choice4 },
      );
    },
  );
}

const def = (
  id: string,
  pl: string,
  en: string,
  generate: (rng: Rng, tier: 1 | 2 | 3) => QuestionDraft,
  modes: TopicDef["modes"] = ["numeric", "choice4"],
): TopicDef => ({
  id,
  level: "sp7",
  name: text(pl, en),
  modes,
  generate,
});

export const SP7_TOPICS: readonly TopicDef[] = [
  def("sp7.powers_rules", "Działania na potęgach", "Rules of powers", powersRules),
  def("sp7.roots", "Pierwiastki", "Roots", roots),
  def("sp7.sci_notation", "Notacja wykładnicza", "Scientific notation", sciNotation),
  def("sp7.algebra_simplify", "Wyrażenia algebraiczne", "Algebraic expressions", algebraSimplify, [
    "text",
    "choice4",
  ]),
  def("sp7.linear_equations", "Równania liniowe", "Linear equations", linearEquations),
  def("sp7.proportions", "Proporcjonalność", "Proportionality", proportions),
  def("sp7.percent_problems", "Zadania z procentami", "Percent problems", percentProblems),
  def("sp7.pythagoras", "Twierdzenie Pitagorasa", "Pythagorean theorem", pythagoras),
  def("sp7.coordinates", "Układ współrzędnych", "Coordinate plane", coordinates),
  def("sp7.statistics_basic", "Średnia i mediana", "Mean and median", statisticsBasic),
];
